import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import axios from 'axios';
import config from '../../config';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const EditRequestForm = ({ requestId, onRequestUpdated, property, onClose }) => {
  const [propertyAccounts, setPropertyAccounts] = useState([]);
  const [unitAccounts, setUnitAccounts] = useState([]);
  const [formData, setFormData] = useState({
    unit: '',
    requestedBy: '',
    managedBy: [],
    assignees: [],
    type: '',
    status: 'Pending',
    description: '',
    details: {
      title: '',
      issue: '',
      priority: 'High',
      requestedDate: '',
    },
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log("EditRequestForm mounted with requestId:", requestId);
  }, [requestId]);

  useEffect(() => {
    const fetchRequestData = async () => {
      try {
        const response = await axios.get(`${config.API_URL}/v2/request/${requestId}`, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});
        setFormData((prevData) => ({
          ...prevData,
          ...response.data,
          details: {
            title: response.data?.details?.title || '',
            issue: response.data?.details?.issue || '',
            priority: response.data?.details?.priority || 'High',
            requestedDate: response.data?.details?.requestedDate || '',
          },
        }));

      } catch (error) {
        console.error('Error fetching request data:', error);
        setError('Error fetching request data. Please try again.');
      }
    };

    fetchRequestData();
  }, [requestId]);

useEffect(() => {
  if (!formData?.unit?._id) return;
  const fetchUnitAccounts = async () => {
    try {
      const response = await axios.get(
        `${config.API_URL}/v2/role/unitRole/unit/${formData.unit._id}`, 
        {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}}
      );
      setUnitAccounts(response.data.map(item => item.propertyRole.account)); 
    } catch (err) {
      console.error("Error fetching unit accounts:", err);
      setError("Error fetching unit accounts. Please try again.");
    }
  };
  fetchUnitAccounts();
}, [formData.unit?._id]);

useEffect(() => {
  if (!property?.selectedProperty.value) return;
  const fetchPropertyAccounts = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/v2/role/propertyRole/property/${property?.selectedProperty.value}`, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});
      console.log("Response data for property accounts:", response.data);
      setPropertyAccounts(response.data.map(item => item.account));
      console.log("Fetched property accounts:", response.data.map(item => item.account));
    } catch (err) {
      console.error("Error fetching property accounts:", err);
      setError("Error fetching property accounts. Please try again.");
    }
  };
  fetchPropertyAccounts();
}, [property?.selectedProperty?.value]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    console.log("Editing request...");

    // Extract only the _id values of the selected accounts
    const updatedFormData = {
      ...formData,
      managedBy: formData.managedBy.map(account => account._id), // Only send _id values
      assignees: formData.assignees.map(account => account._id), // Only send _id values
    };

    try {
      const response = await axios.put(`${config.API_URL}/v2/request/${requestId}`, updatedFormData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}`, 'Content-Type': 'application/json' },
      });

      if (response.status === 200 || response.status === 201) {
        onRequestUpdated(response.data);
        onClose();
        toast.success('Request updated successfully!');
      } else {
        setError('Failed to update the request.');
        toast.error('Failed to update the request.');
      }
    } catch (error) {
      setError('Error updating the request. Please try again.');
      console.error(error);
      toast.error('Error updating the request. Please try again.');
    }
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <button className="close-popup" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
        <h2 className="text-2xl text-black">Edit Request</h2>
        <div className="popup-columns">
          <Autocomplete
            id="requestedBy"
            name="requestedBy"
            options={unitAccounts}
            getOptionLabel={(option) => `(${option.type}) ${option.user.email}`}
            value={
              unitAccounts.find((account) => String(account._id) === String(formData.requestedBy._id)) || null
            }
            onChange={(event, newValue) => {
              setFormData((prevData) => ({
                ...prevData,
                requestedBy: newValue ? newValue._id : '',
              }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Requested By" variant="outlined" fullWidth required />
            )}
            sx={{ width: '100%', marginBottom: '1rem' }}
          />
          <Autocomplete
            name="priority"
            value={formData.details.priority || ''}
            onChange={(event, newValue) => {
              if (newValue) {
                setFormData((prevData) => ({
                  ...prevData,
                  details: {
                    ...prevData.details,
                    priority: newValue,
                  },
                }));
              }
            }}
            options={['High', 'Medium', 'Low']}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Priority"
                variant="outlined"
                fullWidth
                required
              />
            )}
            sx={{ width: '100%' }}
          />
        </div>
        <TextField
          label="Request Title"
          name="title"
          fullWidth
          value={formData.details.title}
          onChange={(event) => {
            const newValue = event.target.value;
            setFormData((prevData) => ({
              ...prevData,
              details: {
                ...prevData.details,
                title: newValue,
              },
            }));
          }}
          sx={{ marginBottom: '1rem' }}
          required
        />

        <Autocomplete
          multiple
          id="managedBy"
          name="managedBy"
          options={propertyAccounts.filter((acc) => acc.type === "manager")}
          getOptionLabel={(option) => `(${option.type}) ${option.user.email}`}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          value={propertyAccounts.filter((acc) =>
            formData.managedBy.some((manager) => manager._id === acc._id)
          )}
          onChange={(event, newValue) => {
            setFormData((prevData) => ({
              ...prevData,
              managedBy: newValue, // Store the full object
            }));
          }}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField
              {...params}
              label="Managed By"
              placeholder="Select Users"
              variant="outlined"
              fullWidth
              required
            />
          )}
          sx={{ width: '100%', marginBottom: '1rem' }}
        />
        <Autocomplete
          multiple
          id="assignees"
          name="assignees"
          options={propertyAccounts.filter(
            (acc) => acc.type !== "owner" && acc.type !== "tenant"
          )}
          getOptionLabel={(option) => `(${option.type}) ${option.user.email}`}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          value={propertyAccounts.filter((acc) =>
            formData.assignees.some((assignee) => assignee._id === acc._id)
          )}
          onChange={(event, newValue) => {
            setFormData((prevData) => ({
              ...prevData,
              assignees: newValue, // Store the full object
            }));
          }}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField
              {...params}
              label="Assignees"
              placeholder="Select Users"
              variant="outlined"
              fullWidth
              required
            />
          )}
          sx={{ width: '100%', marginBottom: '1rem' }}
        />
        <div className="popup-columns">
          <Autocomplete
            name="type"
            value={formData.type ? formData.type.charAt(0).toUpperCase() + formData.type.slice(1) : ''}
            onChange={(event, newValue) => {
              if (newValue) {
                setFormData((prevData) => ({
                  ...prevData,
                  type: newValue,
                }));
              }
            }}
            options={['Maintenance', 'Package', 'ChangeRequest', 'Complaint', 'AmenityBooking', 'Custom', 'Payment', 'Service']}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Type"
                variant="outlined"
                fullWidth
                required
              />
            )}
            sx={{ width: '100%', marginBottom: '1rem' }}
          />
          <Autocomplete
            name="status"
            value={formData.status || ''}
            onChange={(event, newValue) => {
              if (newValue) {
                setFormData((prevData) => ({
                  ...prevData,
                  status: newValue,
                }));
              }
            }}
            options={['Pending', 'In-Progress', 'Completed', 'Rejected']}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Status"
                variant="outlined"
                fullWidth
                required
              />
            )}
            sx={{ width: '100%' }}
          />
        </div>
        <TextField
          label="Description"
          name="description"
          multiline
          fullWidth
          rows={3}
          value={formData.description}
          onChange={handleChange}
          sx={{ marginBottom: '1rem' }}
        />
        <button type="button" className="button" id="edit-request-button" onClick={handleSubmit}>Edit Request</button>
        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
};

export default EditRequestForm;
